import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  FileText,
  Download,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  PenTool,
  Calendar,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

interface PayrollDocument {
  id: string;
  period: string;
  title: string;
  file_url: string;
  file_name: string;
  file_size: number;
  status: 'pending' | 'signed' | 'rejected';
  signed_at: string | null;
  rejection_reason: string | null;
  uploaded_by_name: string;
  created_at: string;
}

const statusConfig = {
  pending: { label: 'Pendiente de firma', color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30', icon: Clock },
  signed: { label: 'Firmado', color: 'bg-green-500/10 text-green-600 border-green-500/30', icon: CheckCircle },
  rejected: { label: 'Rechazado', color: 'bg-red-500/10 text-red-600 border-red-500/30', icon: XCircle },
};

export function PayrollDocumentsList() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [documents, setDocuments] = useState<PayrollDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<PayrollDocument | null>(null);
  const [signing, setSigning] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const fetchDocuments = async () => {
    if (!user) return;

    try {
      const { data, error } = await api
        .from('payroll_documents')
        .select('*')
        .eq('employee_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los documentos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [user]);

  const handleSign = async () => {
    if (!selectedDoc || !user) return;

    setSigning(true);
    try {
      const { error } = await api
        .from('payroll_documents')
        .update({
          status: 'signed',
          signed_at: new Date().toISOString(),
          signed_from_ip: 'web-client',
          signed_user_agent: navigator.userAgent,
        })
        .eq('id', selectedDoc.id);

      if (error) throw error;

      toast({
        title: 'Documento firmado',
        description: 'Has confirmado la recepción de este documento',
      });

      setSelectedDoc(null);
      fetchDocuments();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo firmar el documento',
        variant: 'destructive',
      });
    } finally {
      setSigning(false);
    }
  };

  const handleReject = async () => {
    if (!selectedDoc || !rejectionReason.trim()) {
      toast({
        title: 'Error',
        description: 'Indica el motivo del rechazo',
        variant: 'destructive',
      });
      return;
    }

    setRejecting(true);
    try {
      const { error } = await api
        .from('payroll_documents')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason,
        })
        .eq('id', selectedDoc.id);

      if (error) throw error;

      toast({
        title: 'Documento rechazado',
        description: 'Se ha notificado a administración',
      });

      setSelectedDoc(null);
      setShowRejectDialog(false);
      setRejectionReason('');
      fetchDocuments();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo rechazar el documento',
        variant: 'destructive',
      });
    } finally {
      setRejecting(false);
    }
  };

  const pendingCount = documents.filter(d => d.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Mis Nóminas
        </h3>
        <p className="text-sm text-muted-foreground">
          Documentos de nómina enviados por administración
        </p>
      </div>

      {/* Pending alert */}
      {pendingCount > 0 && (
        <Card className="p-4 bg-yellow-500/10 border-yellow-500/30">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <span className="font-medium text-yellow-700">
              Tienes {pendingCount} documento(s) pendiente(s) de firma
            </span>
          </div>
        </Card>
      )}

      {/* Documents list */}
      {documents.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No tienes documentos de nómina</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {documents.map((doc) => {
            const status = statusConfig[doc.status];
            const StatusIcon = status.icon;

            return (
              <Card
                key={doc.id}
                className={`p-4 transition-all hover:shadow-md ${
                  doc.status === 'pending' ? 'ring-2 ring-yellow-500/30' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-red-500/10">
                      <FileText className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <h4 className="font-medium">{doc.title}</h4>
                      <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {doc.period}
                        </span>
                        <span>·</span>
                        <span>
                          {formatDistanceToNow(new Date(doc.created_at), {
                            addSuffix: true,
                            locale: es,
                          })}
                        </span>
                      </div>
                      {doc.status === 'signed' && doc.signed_at && (
                        <p className="text-xs text-green-600 mt-1">
                          Firmado el {format(new Date(doc.signed_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                        </p>
                      )}
                      {doc.status === 'rejected' && doc.rejection_reason && (
                        <p className="text-xs text-red-600 mt-1">
                          Motivo: {doc.rejection_reason}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={status.color}>
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {status.label}
                    </Badge>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => window.open(doc.file_url, '_blank')}
                      title="Ver documento"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = doc.file_url;
                        link.download = doc.file_name;
                        link.click();
                      }}
                      title="Descargar"
                    >
                      <Download className="w-4 h-4" />
                    </Button>

                    {doc.status === 'pending' && (
                      <Button
                        size="sm"
                        onClick={() => setSelectedDoc(doc)}
                      >
                        <PenTool className="w-4 h-4 mr-1" />
                        Firmar
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Sign Dialog */}
      <Dialog open={!!selectedDoc && !showRejectDialog} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Firmar Documento
            </DialogTitle>
            <DialogDescription>
              Confirma que has recibido y revisado este documento
            </DialogDescription>
          </DialogHeader>

          {selectedDoc && (
            <div className="space-y-4">
              <Card className="p-4 bg-muted/50">
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="font-medium">{selectedDoc.title}</p>
                    <p className="text-sm text-muted-foreground">Periodo: {selectedDoc.period}</p>
                  </div>
                </div>
              </Card>

              <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <p className="text-sm text-green-700">
                  Al firmar este documento confirmas que:
                </p>
                <ul className="list-disc list-inside text-sm text-green-700 mt-2 space-y-1">
                  <li>Has recibido el documento correctamente</li>
                  <li>Has revisado su contenido</li>
                  <li>Aceptas los datos reflejados</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectDialog(true);
              }}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Rechazar
            </Button>
            <Button onClick={handleSign} disabled={signing}>
              {signing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <CheckCircle className="w-4 h-4 mr-1" />
              Firmar Documento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Rechazar Documento
            </DialogTitle>
            <DialogDescription>
              Indica el motivo por el que rechazas este documento
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Textarea
              placeholder="Ej: Los datos no coinciden con mi contrato..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectDialog(false);
                setRejectionReason('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejecting || !rejectionReason.trim()}
            >
              {rejecting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar Rechazo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
